import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Autocomplete,
  Button,
  Grid,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import DetailsModal from '@/components/DetailsModal';
import validations from '@/helpers/validation';
import { DatePickerInput } from '@mantine/dates';
import s3Mutations from '@/api/mutitations/s3';
import Modal from '@/components/Modal/Index';
import { YES_NO } from '@/helpers/enums';
import { toDate } from 'date-fns';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';

const EditAdditionalFee = (props) => {
  const {
    open,
    close,
    opened,
    setKey,
    fee,
    handleAgentUpdateClick,
  } = props;

  const t = prepareTranslate();
  const { useUpload, useDelete } = s3Mutations;
  const [actionResultOpened,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();
  const { mutateAsync: deleteMutate } = useDelete();

  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');
  const [filesToDelete, setFilesToDelete] = useState([]);

  const form = useForm({
    initialValues: {
      ...fee, files: fee?.files || [],
    },
    validate: {
      value: (value) => validations.required(value),
      type: (value) => validations.required(value),
      date: (value) => validations.required(value),
    },
  });

  const handleDeleteFee = () => {
    if (!form.values.deletionReason) {
      form.setFieldError('deletionReason', t('validations.required'));
      return;
    }

    handleAdditionalFeeSave({ ...form.values, status: 'archive', archiveDate: new Date() });
    deleteClose();
  };

  const handleAdditionalFeeSave = async (values) => {
    setLoading(true);
    let files;
    try {
      filesToDelete.forEach((file) => {
        const { path } = file;
        deleteMutate({ path });
      });

      const newFiles = form.values.files?.filter((file) => !file.path);

      const clonedValues = { ...values };

      if (newFiles?.length > 0) {
        const formData = new FormData();

        newFiles.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          files = await uploadMutate(formData);
          const oldFiles = form.values.files.filter((file) => file.path);
          clonedValues.files = [...files.files, ...oldFiles];
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPaid = clonedValues.isPaid === t('global.yes');

      const result = await handleAgentUpdateClick({
        fee: clonedValues,
      });

      if (!result.success) {
        setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
        actionResultOpen();
        form.errors = null;
        return;
      }

      setResultLabel(t('pages.agencyCommissionsEdit.notification.success.title'));
      close();
      actionResultOpen();
      setKey((prev) => prev + 1);
    } catch (error) {
      setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
      actionResultOpen();
      form.errors = null;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    close();
  };

  const actions = [
        <Button
            key='primary'
            loading={loading}
            onClick={form.onSubmit(handleAdditionalFeeSave)}
            w={'7rem'}
        >
            {t('global.update')}
        </Button>,
        <Button
            key='cancel'
            variant='outline'
            onClick={handleCancelClick}
            w={'7rem'}>
            {t('global.cancel')}
        </Button>,
  ];

  const resultActions = [
        <Button
            key='closeButton' onClick={() => actionResultClose()}>
            {t('global.close')}
        </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
    const fileToRemove = form.values.files[index];

    if (fileToRemove?.path) {
      setFilesToDelete((prev) => [...prev, fileToRemove]);
    }
  };

  useEffect(() => {
    if (fee) {
      const {
        isPaid, date, payDate, files,
      } = fee;
      form.setValues({
        ...fee,
        isPaid: isPaid ? t('global.yes') : t('global.no'),
        date: toDate(date),
        payDate: payDate ? toDate(payDate) : null,
        files: Array.isArray(files) ? files : [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fee]);

  const handleCancel = () => {
    deleteClose();
    open();
  };

  const deleteActions = [
        <Button key='delete' w={'6rem'} onClick={handleDeleteFee}>
            {t('global.delete')}
        </Button>,
        <Button key='cancel' w={'6rem'} onClick={handleCancel} variant='outline'>
            {t('global.cancel')}
        </Button>,
  ];

  const handleDelete = () => {
    close();
    deleteOpen();
  };

  const leftActions = [
        <Button
            key='cancel'
            variant='outline'
            onClick={handleDelete}
            w={'7rem'}
        >
            {t('components.editAdditionalFee.delete')}
        </Button>,
  ];

  return (
        <>
            <DetailsModal
                actions={actions}
                leftActions={leftActions}
                open={open}
                close={handleCancelClick}
                opened={opened}
                label={t('components.editAdditionalFee.label')}
            >
                <Grid gutter={{ base: 'xl' }}>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            label={t('components.editAdditionalFee.type')}
                            required
                            limit="10"
                            key={form.key('type')}
                            {...form.getInputProps('type')}
                        />
                        <Autocomplete
                            label={t('components.editAdditionalFee.isPaid')}
                            data={YES_NO}
                            required
                            key={form.key('isPaid')}
                            {...form.getInputProps('isPaid')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            type='number'
                            autoComplete='off'
                            label={t('components.editAdditionalFee.sum')}
                            key={form.key('sum')}
                            maxLength={7}
                            required
                            {...form.getInputProps('value')}
                        />
                        <DatePickerInput
                            valueFormat="DD/MM/YYYY"
                            key={'payDate'}
                            label={t('components.editAdditionalFee.payDate')}
                            {...form.getInputProps('payDate')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <DatePickerInput
                            required
                            valueFormat="DD/MM/YYYY"
                            key={'date'}
                            label={t('components.editAdditionalFee.date')}
                            {...form.getInputProps('date')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            key={'comments'}
                            label={t('components.editAdditionalFee.comments')}
                            {...form.getInputProps('comments')}
                        />
                    </Grid.Col>
              </Grid>
              <UploadFilesButton
                    onFilesSelected={onFileSelected}
                    label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
              />
                {form.values.files?.length > 0 && (
                    <EditFiles
                        files={form.values.files}
                        onNameChange={onFileNameChange}
                        onRemove={onDeleteFile}
                    />
                )}
            </DetailsModal>
            <DetailsModal
                opened={deleteOpened}
                close={handleCancel}
                label={t('components.editAdditionalFee.verifyDeletion')}
                actions={deleteActions}
            >
                <TextInput
                    required
                    {...form.getInputProps('deletionReason')}
                    label={t('components.editAdditionalFee.deletionReason')}
                />
            </DetailsModal>
            <Modal
                opened={actionResultOpened}
                onClose={actionResultClose}
                actions={resultActions}
                label={resultLabel}
            />
        </>
  );
};

EditAdditionalFee.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  fee: PropTypes.obj,
  handleAgentUpdateClick: PropTypes.func,
};

export default EditAdditionalFee;
